import { Helmet } from "react-helmet-async";
import { Phone, MessageCircle, ShieldCheck, Award, Leaf, Clock, ChevronDown, ChevronUp, ArrowLeft, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import FloatingWhatsApp from "@/components/FloatingWhatsApp";

const WHATSAPP_URL = "https://wa.me/918882183014?text=Hi%2C%20I%20want%20to%20book%20underground%20water%20tank%20cleaning%20in%20Gurugram";

const faqs = [
  { q: "How do you clean an underground water tank safely?", a: "Our team follows strict safety protocols including gas testing before entry, proper ventilation setup, use of breathing apparatus, and a buddy system. We use submersible pumps for dewatering and specialized equipment designed for confined space cleaning." },
  { q: "How often should underground tanks be cleaned?", a: "Underground water tanks should be cleaned every 6 months minimum. Due to their enclosed nature and susceptibility to groundwater seepage in Gurugram, quarterly cleaning is recommended for optimal water quality." },
  { q: "What is the cost of underground tank cleaning in Gurugram?", a: "Underground tank cleaning starts at ₹1,499 for tanks up to 2,000 litres. Final pricing depends on tank size, depth, accessibility, and condition. We provide a free assessment before starting." },
  { q: "Do you remove the sludge completely?", a: "Yes. We perform complete sludge removal using industrial vacuum pumps, followed by high-pressure jet cleaning and multi-stage disinfection. The sludge is disposed of responsibly per environmental guidelines." },
  { q: "Can you clean very large underground tanks for societies?", a: "Absolutely. We handle underground tanks from 2,000 litres to 50,000+ litres for RWAs, commercial complexes, and housing societies across Gurugram. We have industrial-grade equipment for large-capacity tanks." },
];

const steps = [
  { title: "Safety Assessment & Gas Testing", desc: "Confined space gas testing, ventilation setup, and safety equipment deployment before any entry." },
  { title: "Dewatering & Sludge Removal", desc: "Industrial submersible pumps drain the tank completely. Vacuum extraction removes all sludge and sediment." },
  { title: "High Pressure Jet Cleaning", desc: "Specialized long-reach jet equipment cleans walls, floor, and ceiling at 200+ bar pressure without manual entry where possible." },
  { title: "Disinfection & Quality Check", desc: "Multi-stage anti-bacterial treatment followed by water quality testing to ensure safety standards are met." },
];

function FAQ({ faq }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between p-4 text-left hover:bg-slate-50 transition-colors">
        <span className="font-medium text-navy text-sm pr-4">{faq.q}</span>
        {open ? <ChevronUp className="w-4 h-4 text-sky flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-sky flex-shrink-0" />}
      </button>
      {open && <div className="px-4 pb-4 text-slate-600 text-sm leading-relaxed">{faq.a}</div>}
    </div>
  );
}

export default function UndergroundTankCleaning() {
  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Underground Water Tank Cleaning in Gurugram | TankBuddy</title>
        <meta name="description" content="Expert underground water tank cleaning in Gurugram. Starting ₹1,499. Confined space certified team, industrial-grade equipment, complete sludge removal. Book TankBuddy!" />
        <meta name="keywords" content="underground tank cleaning Gurugram, underground water tank cleaning Gurgaon, sump cleaning service" />
        <link rel="canonical" href="https://www.tankbuddy.in/underground-water-tank-cleaning-gurugram" />
        <meta property="og:title" content="Underground Water Tank Cleaning in Gurugram | TankBuddy" />
        <meta property="og:description" content="Expert underground water tank cleaning in Gurugram. Starting ₹1,499. Certified team, industrial-grade equipment." />
        <meta property="og:url" content="https://www.tankbuddy.in/underground-water-tank-cleaning-gurugram" />
        <meta name="robots" content="index, follow" />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Underground Water Tank Cleaning in Gurugram",
            "provider": {
              "@type": "LocalBusiness",
              "name": "TankBuddy",
              "telephone": "+91-8882183014",
              "url": "https://www.tankbuddy.in",
              "address": {"@type": "PostalAddress", "addressLocality": "Gurugram", "addressRegion": "Haryana", "addressCountry": "IN"}
            },
            "areaServed": "Gurugram",
            "description": "Expert underground water tank cleaning service in Gurugram with confined space safety protocols and industrial-grade equipment.",
            "offers": {"@type": "Offer", "price": "1499", "priceCurrency": "INR", "priceValidUntil": "2026-12-31"}
          }
        `}</script>
      </Helmet>

      <Header />

      <main className="pt-20 pb-16">
        <section className="max-w-4xl mx-auto px-4 md:px-8 py-12">
          <a href="/" className="inline-flex items-center gap-2 text-sky text-sm font-medium mb-8 hover:underline">
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </a>

          <h1 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl text-navy leading-tight mb-6">
            Expert Underground Water Tank Cleaning in Gurugram
          </h1>

          <div className="flex flex-wrap gap-4 mb-10">
            <span className="inline-flex items-center gap-2 bg-sky/10 text-sky text-sm font-medium px-3 py-1.5 rounded-full"><ShieldCheck className="w-4 h-4" /> Certified Technicians</span>
            <span className="inline-flex items-center gap-2 bg-sky/10 text-sky text-sm font-medium px-3 py-1.5 rounded-full"><Leaf className="w-4 h-4" /> Eco-friendly</span>
            <span className="inline-flex items-center gap-2 bg-sky/10 text-sky text-sm font-medium px-3 py-1.5 rounded-full"><Clock className="w-4 h-4" /> Same-day Service</span>
            <span className="inline-flex items-center gap-2 bg-amber-100 text-amber-800 text-sm font-medium px-3 py-1.5 rounded-full"><AlertTriangle className="w-4 h-4" /> Confined Space Certified</span>
          </div>

          <div className="prose prose-slate max-w-none mb-12">
            <h2 className="font-heading font-bold text-2xl text-navy mb-4">What is Underground Water Tank Cleaning?</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              Underground water tank cleaning is a specialized service that involves safely accessing, draining, and thoroughly cleaning subterranean water storage tanks. These tanks, also called sumps or underground reservoirs, collect groundwater seepage, mud, silt, and organic matter over time. Unlike overhead tanks, underground tanks pose additional challenges due to confined space hazards, poor ventilation, and difficult access points.
            </p>
            <p className="text-slate-600 leading-relaxed mb-4">
              TankBuddy's underground tank cleaning team is trained in confined space operations with proper safety certifications. We use industrial submersible pumps for complete dewatering, vacuum extraction systems for sludge removal, and long-reach high-pressure jet equipment that can clean tank surfaces without requiring full human entry. For Gurugram residents, where underground tanks often serve as primary storage receiving bore well or tanker water, regular professional cleaning is critical for safe water supply.
            </p>

            <h2 className="font-heading font-bold text-2xl text-navy mb-4 mt-10">Why Underground Tank Cleaning is Critical</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              Underground tanks in Gurugram face unique contamination challenges. Groundwater seepage can introduce heavy metals, pesticides from nearby agricultural land, and sewage contamination from aging municipal infrastructure. The dark, enclosed environment accelerates anaerobic bacterial growth, producing toxic gases like hydrogen sulfide.
            </p>
            <p className="text-slate-600 leading-relaxed">
              Without regular cleaning, underground tanks accumulate thick layers of sludge that reduce storage capacity by up to 30%, harbor disease-causing organisms, and produce foul-smelling water. Professional cleaning every 6 months eliminates these hazards, restores full capacity, and ensures the water pumped to your overhead tank is already clean at the source.
            </p>
          </div>

          <div className="bg-surface border border-slate-200 rounded-xl p-8 mb-12">
            <h2 className="font-heading font-bold text-2xl text-navy mb-2">Our Safety-First Cleaning Process</h2>
            <p className="text-slate-500 text-sm mb-6">Confined space protocols followed at every step</p>
            <div className="grid md:grid-cols-2 gap-6">
              {steps.map((s, i) => (
                <div key={s.title} className="flex gap-4">
                  <div className="w-8 h-8 rounded-lg bg-sky/10 flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-sky font-bold text-sm">{i + 1}</span>
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-navy mb-1">{s.title}</h3>
                    <p className="text-slate-600 text-sm leading-relaxed">{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-navy rounded-xl p-8 mb-12 text-white">
            <h2 className="font-heading font-bold text-2xl mb-2">Pricing</h2>
            <p className="text-slate-300 mb-4">Underground tank cleaning in Gurugram</p>
            <div className="text-4xl font-heading font-extrabold mb-1">Starting ₹1,499</div>
            <p className="text-slate-400 text-sm mb-6">For tanks up to 2,000 litres. Includes safety protocols, sludge removal, and disinfection.</p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button asChild className="bg-whatsapp hover:bg-whatsapp-hover text-white rounded-full px-6">
                <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer"><MessageCircle className="w-4 h-4 mr-2" /> Book on WhatsApp</a>
              </Button>
              <Button asChild variant="outline" className="border-white text-white hover:bg-white/10 rounded-full px-6">
                <a href="tel:+918882183014"><Phone className="w-4 h-4 mr-2" /> Call +91 88821 83014</a>
              </Button>
            </div>
          </div>

          <div className="mb-12">
            <h2 className="font-heading font-bold text-2xl text-navy mb-6">Frequently Asked Questions</h2>
            <div className="space-y-3">
              {faqs.map((faq, i) => <FAQ key={i} faq={faq} />)}
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <FloatingWhatsApp />
    </div>
  );
}
